import {
  CollectionViewer,
  DataSource,
  SelectionModel,
} from '@angular/cdk/collections';
import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { MatSort } from '@angular/material/sort';

import { Store } from '@ngrx/store';
import { BehaviorSubject, merge, Observable, of, startWith } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { paginatorConfiguration } from '@shared/constants/paginator-configuration';
import { PageRequest } from '@shared/interfaces/page-request.interface';
import { PageResponse } from '@shared/interfaces/page-response.interface';

export class TableDataSource<TFilter extends PageRequest, T>
  implements DataSource<T>
{
  displayedColumns: string[] = [];
  pageSizeOptions = paginatorConfiguration.pageSizeOptions;
  isLoading = new BehaviorSubject<boolean>(false);
  get data(): T[] {
    return this.dataSubject.value;
  }
  selection = new SelectionModel<T>(true, []);

  private paginator: MatPaginator | null = null;
  private matSort!: MatSort;
  private filterParams: TFilter | undefined;
  private dispatchGetListAction!: (filter: TFilter, store: Store) => void;
  private dispatchUpdateFilter!: (filter: TFilter, store: Store) => void;
  private selectList!: (store: Store) => Observable<PageResponse<any>>;
  private selectListIsLoading!: (store: Store) => Observable<any>;
  private dataSubject = new BehaviorSubject<T[]>([]);
  private initialFilter!: TFilter;

  constructor(private store: Store) {}

  init(
    paginator: MatPaginator | null,
    matSort: MatSort | null,
    filterParams: any | undefined,
    displayedColumns: string[],
    dispatchGetListAction: (filter: TFilter, store: Store) => void,
    dispatchUpdateFilter: (filter: TFilter, store: Store) => void,
    selectList: (store: Store) => Observable<PageResponse<any>>,
    selectListIsLoading: (store: Store) => Observable<any>
  ): void {
    this.paginator = paginator;
    this.matSort = matSort ?? new MatSort();
    this.initialFilter = filterParams;
    this.displayedColumns = displayedColumns;
    this.dispatchGetListAction = dispatchGetListAction;
    this.dispatchUpdateFilter = dispatchUpdateFilter;
    this.selectList = selectList;
    this.selectListIsLoading = selectListIsLoading;

    merge(this.paginator?.page ?? of({}), this.matSort.sortChange)
      .pipe(startWith({}), distinctUntilChanged())
      .subscribe(() => {
        this.selection.clear();
        this.updateFilter();
      });
  }

  private load(filter: TFilter): void {
    this.dispatchGetListAction(filter, this.store);
    this.selectList(this.store)
      .pipe(debounceTime(100), distinctUntilChanged())
      .subscribe((response) => {
        if (response) {
          this.dataSubject.next(response.content);
          if (this.paginator) {
            this.paginator.length = response.totalElements;
            this.paginator.pageIndex = response?.pageable?.pageNumber;
          }
        } else {
          this.dataSubject.next([]);
          if (this.paginator) {
            this.paginator.length = 0;
            this.paginator.pageIndex = 0;
          }
        }
        this.selection.clear();
      });

    this.selectListIsLoading(this.store).subscribe((isLoading) => {
      this.isLoading.next(isLoading);
    });
  }

  private updateFilter(): void {
    const filter = (
      this.filterParams ? { ...this.filterParams } : { ...this.initialFilter }
    ) as TFilter;
    filter.page = this.paginator?.pageIndex ?? 0;
    filter.size = this.paginator?.pageSize ?? 9999;
    filter.sort = this.matSort.active
      ? [`${this.matSort.active},${this.matSort.direction}`]
      : undefined;
    this.dispatchUpdateFilter(filter, this.store);
    this.load(filter);
  }

  connect(collectionViewer: CollectionViewer): Observable<T[]> {
    return this.dataSubject.asObservable();
  }

  disconnect(collectionViewer: CollectionViewer): void {
    this.dataSubject.complete();
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.data.length;
    return numSelected === numRows;
  }

  toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }

    this.selection.select(...(this.dataSubject.value as []));
  }

  filterData(filter?: TFilter) {
    this.filterParams = filter;
    this.updateFilter();
  }
}
